import React from 'react';
import { Checkbox } from '@mui/material';
import { colors } from '../../themes/typography';

/**
 * BlazingFastCheckbox Component
 * 
 * High-performance checkbox with direct DOM manipulation for instant visual feedback.
 * Used in experiment cards for selection functionality.
 * 
 * @param {string|number} experimentId - Unique identifier for the experiment
 * @param {boolean} isSelected - Current selection state
 * @param {Function} onToggleSelection - Callback when checkbox is toggled
 */
const BlazingFastCheckbox = ({ 
  experimentId,
  isSelected,
  onToggleSelection 
}) => {
  const checkboxRef = React.useRef();
  const [internalSelected, setInternalSelected] = React.useState(isSelected);
  
  // Direct DOM update for instant visual feedback
  const handleChange = React.useCallback((e) => {
    e.stopPropagation();
    
    // BLAZING FAST: Direct DOM manipulation for instant feedback
    const newState = !internalSelected;
    setInternalSelected(newState);
    
    // Update checkbox immediately without waiting for React
    if (checkboxRef.current) {
      checkboxRef.current.checked = newState;
    }
    
    // CRITICAL: Make selection state update synchronous to prevent race conditions
    // The dropdown needs the selection state to be immediately available
    onToggleSelection(experimentId);
  }, [internalSelected, onToggleSelection, experimentId]);
  
  // Sync with external state only when necessary
  React.useLayoutEffect(() => {
    if (internalSelected !== isSelected) {
      setInternalSelected(isSelected);
      if (checkboxRef.current) {
        checkboxRef.current.checked = isSelected;
      }
    }
  }, [isSelected]);
  
  return (
    <Checkbox
      ref={checkboxRef}
      checked={internalSelected}
      onChange={handleChange}
      onClick={(e) => e.stopPropagation()}
      sx={{
        p: '2px',
        color: '#6b7280',
        transition: 'none', // Remove transition for instant response
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          borderRadius: '4px',
        },
        '&.Mui-checked': {
          color: colors.action.primary,
        },
        '& .MuiSvgIcon-root': {
          fontSize: '16px'
        }
      }}
    />
  );
};

export default BlazingFastCheckbox;